import React, { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import Modal from 'react-bootstrap-modal'
import DatePicker from 'react-datepicker'
import DateInput from './inputs/date-input'
import PhoneInputWrapper from './inputs/phone-input-wrapper'
import TextInputWrapper from './inputs/text-input-wrapper'
import SelectInputWrapper from './inputs/select-input-wrapper'
import Avatar from './avatar'
import JoinedOn from './joined_on'
import FlashAlert from '../flash-alert'
import { ajaxGet, patchForm, ajaxDelete } from '../../ajax'
import moment from 'moment'
import { formatPhoneNumberIntl } from 'react-phone-number-input'
import History from './history'
import Credits from './credits'

export default function PlayerModal({playerId, onClose, onUpdate, onDelete}) {
  const [player, setPlayer] = useState(null)
  const [regions, setRegions] = useState([])
  const [phone, setPhone] = useState()
  const [countryCode, setCountryCode] = useState()
  const { register, control, reset, handleSubmit, setValue } = useForm({})
  const [alert, setAlert] = useState(null)
  const [showBannedUntil, setShowBannedUntil] = useState()
  const [showUnderage, setShowUnderage] = useState(false)

  useEffect(() => {
    loadPlayer()
  }, [])

  useEffect(() => {
    if (player) {
      reset(
        _.omit(player, 'id', 'image_url', 'display_name', 'joined_on', 'declined_payments_count', 
          'region_name', 'events', 'fake', 'credit_history', 'has_app')
      )
      setPhone(player.phone)
      setCountryCode(player.country_code)
      setShowBannedUntil(player.banned)
      setShowUnderage(!player.waiver_submitted)
    }
  }, [player])

  const loadPlayer = () => {
    ajaxGet(`/admins/players/${playerId}.json`)
    .then(response => { if (response.ok) return response.json() })
    .then((json) => {
      setPlayer(json.player)
      setRegions(json.regions)
    })
  }

  const deletePlayer = async () => {
    const response = await ajaxDelete(`/admins/players/${playerId}`)
    const json = await response.json()
    if (json.alert)
      setAlert(json.alert)
    else {
      onDelete(playerId)
    }
  }

  const onSubmit = (data) => {
    let formData = new FormData()
    for (const key in data) { if (data[key] != null) formData.append(`player[${key}]`, data[key]) }
    if ('birthday' in data && !data.birthday) formData.append('player[birthday]', '')
    if ('banned_until' in data && !data.banned_until) formData.append('player[banned_until]', '')
    formData.append('player[country_code]', countryCode)

    patchForm(`/admins/players/${playerId}.json`, formData)
    .then(response => { return response.json() })
    .then((json) => {
      if (json.alert)
        setAlert(json.alert)
      else {
        const newPlayer = json.player
        setPlayer(newPlayer)
        if (!data.image) onUpdate(newPlayer)
      }
    })
  }

  const onDeleteClicked = async () => {
    if (window.confirm('Are you sure you want to delete this player? The action cannot be undone')) {
      deletePlayer()
    }
  }

  const onUpdateCredit = (credit, reason, expirationDate, category) => {
    onSubmit({credit: credit, credit_reason: reason, expiration_date: expirationDate, category: category})
  }

  const onFileLoad = (file) => {
    onSubmit({image: file})
  }

  if (!player || !regions.length) return null

  const skillLevelOptions = ['Beginner', 'Intermediate', 'Advanced', 'Expert'].map((val, i) => [i, val])
  const genderOptions = [['male', 'Male'], ['female', 'Female']]

  const underage = () => moment(player.birthday, 'MM/DD/YYYY') > moment().subtract(18, 'years')
  const underageUntil = () => moment(player.birthday, 'MM/DD/YYYY').add(18, 'years').format('MM/DD/YYYY')

  return <Modal container={document.body} show={true} onHide={onClose} dialogClassName='modal-xs' className='player-profile'>
    <Modal.Header closeButton>
      <Modal.Title>Player Information</Modal.Title>
    </Modal.Header>
    <ul className='nav nav-tabs'>
      <li role='presentation' className='active'>
        <a data-toggle='tab' href='#player-info'>Profile</a>
      </li>
      <li role='presentation'>
        <a data-toggle='tab' href='#player-history'>History</a>
      </li>
      <li role='presentation'>
        <a data-toggle='tab' href='#player-credits'>Game Credits</a>
      </li>
    </ul>
    <div className='tab-content'>
      <div id='player-info' className='tab-pane fade in active'>
        <Modal.Body>
          { alert && <FlashAlert text={alert.text} type={alert.type} onHide={() => setAlert(null)} />}
          <form className='edit_player' onSubmit={handleSubmit(onSubmit)}>
            <div className='row'>
              <div className='col-lg-9'>
                <TextInputWrapper
                  {...register('first_name')}
                  label='First Name'
                  labelColSize='4'
                  inputColSize='7'
                  onChange={(e) => setValue('first_name', e.target.value)}
                />
                <TextInputWrapper
                  {...register('last_name')}
                  label='Last Name'
                  labelColSize='4'
                  inputColSize='7'
                  onChange={(e) => setValue('last_name', e.target.value)}
                />
                <PhoneInputWrapper
                  {...register('phone')}
                  labelColSize='4'
                  inputColSize='7'
                  defaultValue={formatPhoneNumberIntl(player.phone)}
                  phone={phone}
                  onChange={(p, c) => {
                    setValue('phone', p)
                    setPhone(p)
                    setCountryCode(c.toUpperCase())
                  }}
                />
                <div className='form-group row'>
                  <label className='control-label col-lg-4' htmlFor='birthday'>Date of Birth</label>
                  <div className='col-lg-7'>
                    <Controller
                      control={control}
                      name='birthday'
                      render={({ field }) => (
                        <DatePicker
                          customInput={<DateInput />}
                          onChange={(date) => field.onChange(date && date.format('MM/DD/YYYY'))}
                          selected={moment(new Date(field.value))}
                        />
                      )}
                    />
                  </div>
                </div>
                <TextInputWrapper
                  {...register('email')}
                  label='Email'
                  labelColSize='4'
                  inputColSize='7'
                  onChange={(e) => setValue('email', e.target.value)} />
              </div>
              <Avatar path={player.image_url || '/images/profile-image-placeholder.png'} onFileLoad={onFileLoad} />
              <div className='col-lg-12'>
                <SelectInputWrapper
                  {...register('region_id')}
                  label='Region'
                  options={regions}
                  labelColSize='3'
                  inputColSize='9'
                  onChange={e => setValue('region_id', e.target.value)}
                />
                <TextInputWrapper
                  {...register('nationality')}
                  label='Nationality'
                  labelColSize='3'
                  inputColSize='9'
                  onChange={(e) => setValue('nationality', e.target.value)}
                />
                <SelectInputWrapper
                  {...register('skill_level')}
                  label='Skill level'
                  options={skillLevelOptions}
                  labelColSize='3'
                  inputColSize='9'
                  onChange={(e) => setValue('skill_level', e.target.value)}
                />
                <JoinedOn value={player.joined_on} />
                <div className='row'>
                  <div className='col-lg-3'></div>
                  <div className='col-lg-1 form-check form-inline captain-block'>
                    <label>Captain</label>
                    <input {...register('captain')} type='checkbox'></input>
                  </div>
                  <div className='col-lg-2 form-check form-inline free-games-block'>
                    <label>Free Games</label>
                    <input {...register('free_games')} type='checkbox'></input>
                  </div>
                  <div className='col-lg-2 form-input form-input-inline'>
                    <label>Game Credits
                      <input {...register('credit')} disabled={true} className='form-control' type='number'/>
                    </label>
                  </div>
                  <div className='col-lg-2 form-check form-inline'>
                    <label>Opted Out</label>
                    <input {...register('opted_out')} type='checkbox'></input>
                  </div>
                  <div className='col-lg-2 form-inline text-center'>
                    <label>Declined Payments</label>
                    <label>{player.declined_payments_count}</label>
                  </div>
                </div>
                <div className='row'>
                  <div className='col-lg-3'></div>
                  <div className='col-lg-1 form-check form-inline'>
                    <label>Banned</label>
                    <input
                      {...register('banned')}
                      type='checkbox'
                      onChange={e => {
                        setValue('banned', e.target.checked)
                        setShowBannedUntil(e.target.checked)
                      }}>
                    </input>
                  </div>
                  { showBannedUntil && 
                    <div className='col-lg-3 banned-until'>
                      <label>Banned Until</label>
                      <Controller
                        control={control}
                        name='banned_until'
                        render={({ field }) => (
                          <DatePicker
                            customInput={<DateInput />}
                            minDate={moment()}
                            onChange={(date) => field.onChange(date && date.format('MM/DD/YYYY'))}
                            selected={field.value && moment(new Date(field.value))}
                          />
                        )}
                      />
                    </div>
                  }
                  <div className='col-lg-2 form-check form-inline'>
                    <label>Devices banned</label>
                    <input {...register('devices_banned')} type='checkbox'></input>
                  </div>
                  <div className='col-lg-2 form-check form-inline'>
                    <label>Waiver received</label>
                    <input
                      {...register('waiver_submitted')}
                      type='checkbox'
                      onChange={e => {
                        setValue('waiver_submitted', e.target.checked)
                        setShowUnderage(!e.target.checked)
                      }}>
                    </input>
                  </div>
                </div>
                <br/>
                { showUnderage && underage() &&
                  <div className='text-center alert alert-warning'>
                    The player is underage and will be banned until {underageUntil()}
                  </div>
                }
                <SelectInputWrapper
                  {...register('gender')}
                  label='Gender'
                  options={genderOptions}
                  labelColSize='3'
                  inputColSize='9'
                  onChange={(e) => setValue('gender', e.target.value)}
                />
                <TextInputWrapper
                  {...register('instagram')}
                  label='Instagram'
                  labelColSize='3'
                  inputColSize='9'
                  onChange={(e) => setValue('instagram', e.target.value)}
                />
                <div className='form-group row'>
                  <label className='control-label col-lg-3' htmlFor='info'>Notes</label>
                  <div className='col-lg-9'>
                    <textarea
                      {...register('info')}
                      rows='5'
                      className='form-control'
                      onChange={(e) => setValue('info', e.target.value)}
                    />
                  </div>
                </div>
                <div className='form-group row'>
                  <div className='col-lg-offset-3 col-lg-6'>
                    <input type='submit' value='Save' className='btn btn-primary' />
                  </div>
                  { App.web_config.allow_deleting_players &&
                    <div className='col-lg-3 text-right'>
                      <span className='btn btn-danger' onClick={onDeleteClicked}>Delete</span>
                    </div>
                  }
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
      </div>
      <div id='player-history' className='tab-pane fade'>
        <Modal.Body>
          <History events={player.events} />
        </Modal.Body>
      </div>
      <div id='player-credits' className='tab-pane fade player-credits-tab'>
        <Modal.Body>
          <Credits
            player={player}
            histories={player.credit_history}
            onUpdateCredit={onUpdateCredit}
          />
        </Modal.Body>
      </div>
    </div>
  </Modal>
}
